/**
 * InstallBanner — the "add to your home screen" invitation. Two surfaces show
 * it, and which app a tap installs depends on the manifest linked at that
 * moment: a gym page with `gymPwa` on offers the GYM app, the gym selector
 * offers the whole of Sendly. Both are wanted — see `useInstallBanner` for the
 * state behind it and for why one dismiss silences both.
 *
 * Same floating card as <UpdateBanner>, in the same <TopBannerHost> strip, and
 * for the same reason. On a gym page it used to be an in-flow strip rendered
 * right after the spacer that holds the place of the FIXED header block
 * (`cdf124a`) — so on screen it hung BELOW the header, the tab bar and the map
 * search row, and scrolled away with the page. Before the header went fixed it
 * had been the first thing on the page; the move was a side effect of that
 * fix, never a decision. As a pinned card it is back at the top edge and stays
 * there.
 *
 * Three shapes, by what the browser offers:
 *   - `installPrompt` captured (Chrome / Edge / Android) → the install button,
 *     which calls the stashed `beforeinstallprompt` event.
 *   - iOS → no such event exists, so a line of text explains the Share →
 *     "Add to Home Screen" route instead.
 *   - neither → the invitation and the close button alone.
 */
import React from "react";
import { TopBanner, TopBannerCard, TopBannerAction, TOP_BANNER_ORDER } from "./TopBannerHost";
import { IconBtn, IconClose } from "./ui";
import { useTranslation } from "../i18n";

export function InstallBanner({ show, gymName, installPrompt, isIOS, onInstall, onDismiss }) {
  const { t } = useTranslation();
  if (!show) return null;
  return (
    <TopBanner order={TOP_BANNER_ORDER.install}>
      <TopBannerCard>
        <span>{t("pwa.installHint", { name: gymName || "Sendly" })}</span>
        {installPrompt ? (
          <TopBannerAction onClick={onInstall}>{t("pwa.install")}</TopBannerAction>
        ) : isIOS ? (
          <span style={{ fontSize: 12, fontWeight: 400 }}>{t("pwa.iosHint")}</span>
        ) : null}
        <IconBtn
          icon={IconClose} size={16} padding={4}
          onClick={onDismiss}
          title={t("common.close")}
          style={{ color: "#fff", opacity: 0.7 }}
        />
      </TopBannerCard>
    </TopBanner>
  );
}
